import React, { useState, useCallback } from 'react';
import { exceedsDecimals } from '../../utils/amountFormat';
import { PRICE_DECIMALS, stepFor, formatFigure } from '../../utils/precision';

// Sprint 2026-05-23 — Editable Unit Price Cell
//
// Pattern A from the accounting discussion:
//   • Line price is editable on EVERY line item (linked or manual).
//   • For LINKED products, the FIRST edit per line per session blocks
//     on a confirmation modal that explains the override is per-document
//     and does NOT mutate the catalog.
//   • After confirmation, subsequent edits on that same line go through
//     silently (the user has already accepted the override for that line).
//   • Selecting a new variant on the line resets the override state so
//     the warning fires again if the user re-overrides the new variant.
//   • Manual lines (no linked product) never show the modal — there is
//     no catalog to override against.
//   • A small "Overridden" badge appears under the input for linked +
//     overridden lines so reviewers can spot non-catalog prices at a
//     glance.
//
// IMPORTANT: this component intentionally does NOT write back to the
// StockItemVariant. The product master is the source of truth for the
// CATALOG default. Each transactional document (Pro Forma, Invoice, PO,
// Credit / Debit Note) carries its OWN per-line price snapshot. Silently
// updating the catalog from a transactional document would:
//   1. Retroactively change prices on other in-flight drafts referencing
//      the same variant.
//   2. Break the audit chain (no who / when / why / from / to).
//   3. Bypass the currency check (line currency may differ from variant
//      ``price_currency``).
// If you ever want catalog updates from documents, do it as a separate
// post-save prompt (Pattern B in the discussion), not a silent side
// effect.

const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
};

const modalStyle = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '24px',
    maxWidth: '480px',
    width: '90%',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
    border: '1px solid #e5e7eb',
};

const headingStyle = {
    fontSize: '15px',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
};

const bodyStyle = {
    fontSize: '13px',
    lineHeight: 1.55,
    color: '#374151',
    marginBottom: '16px',
};

const detailRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    marginBottom: '6px',
    fontSize: '12px',
    color: '#374151',
};

const buttonRowStyle = {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    marginTop: '16px',
};

const cancelButtonStyle = {
    padding: '8px 16px',
    border: '1px solid #d1d5db',
    backgroundColor: '#fff',
    color: '#374151',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
};

const confirmButtonStyle = {
    padding: '8px 16px',
    border: '1px solid #b45309',
    backgroundColor: '#d97706',
    color: '#fff',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
    fontWeight: 500,
};

// In-flow below the input. The row-level CSS ``vertical-align: top``
// on ``.line-items-table tbody td`` (OutgoingInvoice.css) keeps all
// inputs aligned at the top of the row so the badge below this
// cell's input does NOT shift the input upward relative to badge-less
// inputs in the same row. Don't absolute-position this — at top: 100%
// it overflows into the next row.
const badgeStyle = {
    display: 'inline-block',
    marginTop: '3px',
    padding: '1px 6px',
    fontSize: '9px',
    fontWeight: 500,
    color: '#92400e',
    backgroundColor: '#fef3c7',
    border: '1px solid #fde68a',
    borderRadius: '4px',
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
};

// A price for display: at least 2 decimals, and every further decimal the
// price genuinely carries (up to PRICE_DECIMALS), so a 6-decimal price is
// never shown rounded to cents. Exact on the string -- no float. Integer part
// grouped with "," per the amountFormat.js house convention.
const fmt = (val) => {
    if (val === '' || val === null || val === undefined) return '—';
    const figure = formatFigure(val, 2);
    if (figure === '') return String(val);
    return figure.replace(/^(-?)(\d+)/, (match, sign, digits) =>
        `${sign}${digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`);
};

/**
 * EditableUnitPriceCell
 *
 * Props:
 *   value              — current unit_price (string | number)
 *   onConfirmedChange  — (newValue: string) => void. Called AFTER the
 *                        override modal is confirmed (for linked lines)
 *                        OR immediately (for manual lines / already
 *                        overridden lines).
 *   hasLinkedProduct   — bool. True when the line is bound to a
 *                        StockItemVariant via item.product.
 *   isOverridden       — bool. Parent-owned. True after the first
 *                        confirmed override on this line in this
 *                        session.
 *   disabled           — bool. Whole-form readOnly toggle.
 *   className          — input className (defaults to ``table-input-number``)
 *   style              — input style overrides (defaults to fontSize 10px)
 *   productLabel       — string. Human-readable name of the linked
 *                        product, surfaced in the modal so the user
 *                        knows which line they are overriding.
 *   currency           — string. Line currency, surfaced in the modal.
 *   inputId            — optional id for the underlying <input>.
 *   onBlur             — optional () => void. Forwarded to the input's
 *                        onBlur so screens that do per-line validation
 *                        on blur (e.g. IncomingInvoiceScreen2) keep
 *                        that behaviour.
 *   maxDecimals        — decimal places the backend price column can
 *                        store. Defaults to PRICE_DECIMALS
 *                        (utils/precision.js), the one number the
 *                        InvoiceLineItem.unit_price and
 *                        PurchaseOrderLineItem.unit_price columns are
 *                        built from. A value with more decimals 400s
 *                        the entire save.
 */
const EditableUnitPriceCell = ({
    value,
    onConfirmedChange,
    hasLinkedProduct,
    isOverridden,
    disabled,
    className = 'table-input-number',
    style,
    productLabel,
    currency,
    inputId,
    onBlur,
    maxDecimals = PRICE_DECIMALS,
}) => {
    const [pendingValue, setPendingValue] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const handleChange = useCallback(
        (e) => {
            const newVal = e.target.value;

            // The line stores a price to ``maxDecimals`` places. A further
            // decimal makes the API refuse the WHOLE payload, so refuse the
            // keystroke instead of letting the operator build a value that
            // cannot be saved. Refuse, never truncate: a quietly shortened
            // price is a wrong price nobody sees.
            if (exceedsDecimals(newVal, maxDecimals)) return;

            // Number inputs fire onChange with an empty string when
            // the user types a non-numeric character that the browser
            // rejects (e.g. ``e``, ``+``, ``-``, letters in some
            // browsers) or when they delete the value entirely. Don't
            // surface the override warning for those — only prompt on
            // an actual numeric edit.
            const parsed = parseFloat(newVal);
            const isRealNumber = newVal !== '' && !Number.isNaN(parsed);

            // Manual line OR already-overridden line OR non-numeric
            // input — write straight through, no modal.
            if (!hasLinkedProduct || isOverridden || !isRealNumber) {
                onConfirmedChange(newVal);
                return;
            }

            // Linked line, first NUMERIC edit — buffer + confirm.
            setPendingValue(newVal);
            setShowModal(true);
        },
        [hasLinkedProduct, isOverridden, onConfirmedChange, maxDecimals],
    );

    const handleConfirm = useCallback(() => {
        if (pendingValue !== null) {
            onConfirmedChange(pendingValue);
        }
        setPendingValue(null);
        setShowModal(false);
    }, [pendingValue, onConfirmedChange]);

    const handleCancel = useCallback(() => {
        setPendingValue(null);
        setShowModal(false);
    }, []);

    const handleOverlayClick = useCallback(
        (e) => {
            if (e.target === e.currentTarget) handleCancel();
        },
        [handleCancel],
    );

    return (
        <>
            <input
                id={inputId}
                type="number"
                step={stepFor(maxDecimals)}
                min="0"
                className={className}
                style={{ fontSize: '10px', ...(style || {}) }}
                value={
                    value === null || value === undefined
                        ? ''
                        // Read-only: show the price without the server's
                        // phantom zeros ('512.340000' -> '512.34'). While
                        // editable the raw value stays, so typing is never
                        // reformatted under the operator's cursor.
                        : disabled ? formatFigure(value, 2) : value
                }
                onChange={handleChange}
                onBlur={onBlur}
                onKeyPress={(e) => {
                    // type="number" alone still lets ``e``, ``+``, ``-``
                    // and letters through in some browsers (they emit an
                    // empty value, which fires onChange and surfaces the
                    // override modal with no real edit). Mirror the
                    // quantity input's hard gate: digits and a single
                    // decimal point only.
                    if (!/[0-9.]/.test(e.key)) {
                        e.preventDefault();
                    }
                }}
                readOnly={disabled}
                placeholder="0.00"
            />
            {hasLinkedProduct && isOverridden && (
                <div>
                    <span style={badgeStyle}>Price overridden</span>
                </div>
            )}

            {showModal && (
                <div style={overlayStyle} onClick={handleOverlayClick}>
                    <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                        <div style={headingStyle}>
                            <span style={{ color: '#d97706', fontSize: '16px' }}>
                                <i
                                    className="fas fa-triangle-exclamation"
                                    aria-hidden="true"
                                />
                            </span>
                            Override catalog price for this line?
                        </div>

                        <div style={bodyStyle}>
                            You&rsquo;re about to change the unit price on a
                            line that is linked to a product in the catalog.
                            This override applies to{' '}
                            <strong>this document only</strong>. The product
                            master record will not be updated, and other
                            documents that reference the same product will
                            continue to use the catalog price.
                        </div>

                        {productLabel && (
                            <div style={detailRowStyle}>
                                <span style={{ color: '#6b7280' }}>Product</span>
                                <span style={{ fontWeight: 500 }}>{productLabel}</span>
                            </div>
                        )}
                        <div style={detailRowStyle}>
                            <span style={{ color: '#6b7280' }}>
                                Current line price
                            </span>
                            <span style={{ fontWeight: 500 }}>
                                {fmt(value)} {currency || ''}
                            </span>
                        </div>
                        <div style={detailRowStyle}>
                            <span style={{ color: '#6b7280' }}>New line price</span>
                            <span style={{ fontWeight: 500, color: '#b45309' }}>
                                {fmt(pendingValue)} {currency || ''}
                            </span>
                        </div>

                        <div style={buttonRowStyle}>
                            <button
                                type="button"
                                style={cancelButtonStyle}
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                style={confirmButtonStyle}
                                onClick={handleConfirm}
                            >
                                Override this line only
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EditableUnitPriceCell;
